import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { BurnListService } from 'src/api/services';
import { BurnListStatus, BurnListRegion, BurnList } from 'src/api/models';

@Injectable({
  providedIn: 'root'
})
export class BurnListDataService {
  // Cached data
  private burnListStatuses: BurnListStatus[] | null = null;
  private burnListRegions: BurnListRegion[] | null = null;
  private burnListTypes: BurnListStatus[] | null = null; // Using BurnListStatus as the type is reused
  private burnLists: Map<number, BurnList[]> = new Map(); // Cached burn lists by customerId

  // Loading states
  private statusesLoading = false;
  private regionsLoading = false;
  private typesLoading = false;
  private burnListsLoading: Map<number, boolean> = new Map(); // Loading states by customerId

  constructor(private burnListService: BurnListService) {}

  /**
   * Get burn list statuses. Returns cached version if available, otherwise loads from API.
   */
  getBurnListStatuses(): Observable<BurnListStatus[]> {
    // If already cached, return immediately
    if (this.burnListStatuses !== null) {
      return of(this.burnListStatuses);
    }

    // If currently loading, wait for that request
    if (this.statusesLoading) {
      return new Observable(observer => {
        const checkInterval = setInterval(() => {
          if (!this.statusesLoading && this.burnListStatuses !== null) {
            clearInterval(checkInterval);
            observer.next(this.burnListStatuses!);
            observer.complete();
          }
        }, 100);
      });
    }

    // Load from API
    this.statusesLoading = true;
    return this.burnListService.BurnListGetBurnListStatuses().pipe(
      tap(statuses => {
        this.burnListStatuses = statuses || [];
        this.statusesLoading = false;
        console.log('Loaded and cached burn list statuses:', this.burnListStatuses);
      }),
      catchError(error => {
        console.error('Failed to load burn list statuses', error);
        this.statusesLoading = false;
        // Return empty array on error but don't cache it
        return of([]);
      })
    );
  }

  /**
   * Get burn list regions. Returns cached version if available, otherwise loads from API.
   */
  getBurnListRegions(): Observable<BurnListRegion[]> {
    // If already cached, return immediately
    if (this.burnListRegions !== null) {
      return of(this.burnListRegions);
    }

    // If currently loading, wait for that request
    if (this.regionsLoading) {
      return new Observable(observer => {
        const checkInterval = setInterval(() => {
          if (!this.regionsLoading && this.burnListRegions !== null) {
            clearInterval(checkInterval);
            observer.next(this.burnListRegions!);
            observer.complete();
          }
        }, 100);
      });
    }

    // Load from API
    this.regionsLoading = true;
    return this.burnListService.BurnListGetBurnListRegions().pipe(
      tap(regions => {
        this.burnListRegions = regions || [];
        this.regionsLoading = false;
        console.log('Loaded and cached burn list regions:', this.burnListRegions);
      }),
      catchError(error => {
        console.error('Failed to load burn list regions', error);
        this.regionsLoading = false;
        // Return empty array on error but don't cache it
        return of([]);
      })
    );
  }

  /**
   * Get burn list types. Returns cached version if available, otherwise loads from API.
   */
  getBurnListTypes(): Observable<BurnListStatus[]> {
    // If already cached, return immediately
    if (this.burnListTypes !== null) {
      return of(this.burnListTypes);
    }

    // If currently loading, wait for that request
    if (this.typesLoading) {
      return new Observable(observer => {
        const checkInterval = setInterval(() => {
          if (!this.typesLoading && this.burnListTypes !== null) {
            clearInterval(checkInterval);
            observer.next(this.burnListTypes!);
            observer.complete();
          }
        }, 100);
      });
    }

    // Load from API
    this.typesLoading = true;
    return this.burnListService.BurnListGetBurnListTypes().pipe(
      tap(types => {
        this.burnListTypes = types || [];
        this.typesLoading = false;
        console.log('Loaded and cached burn list types:', this.burnListTypes);
      }),
      catchError(error => {
        console.error('Failed to load burn list types', error);
        this.typesLoading = false;
        // Return empty array on error but don't cache it
        return of([]);
      })
    );
  }

  /**
   * Get burn lists for a specific customer. Returns cached version if available, otherwise loads from API.
   * @param customerId The customer ID to load burn lists for
   */
  getBurnLists(customerId: number): Observable<BurnList[]> {
    // If already cached for this customer, return immediately
    if (this.burnLists.has(customerId)) {
      return of(this.burnLists.get(customerId)!);
    }

    // If currently loading for this customer, wait for that request
    if (this.burnListsLoading.get(customerId)) {
      return new Observable(observer => {
        const checkInterval = setInterval(() => {
          if (!this.burnListsLoading.get(customerId) && this.burnLists.has(customerId)) {
            clearInterval(checkInterval);
            observer.next(this.burnLists.get(customerId)!);
            observer.complete();
          }
        }, 100);
      });
    }

    // Load from API
    this.burnListsLoading.set(customerId, true);
    return this.burnListService.BurnListGetBurnLists({ customerId }).pipe(
      tap(burnLists => {
        this.burnLists.set(customerId, burnLists || []);
        this.burnListsLoading.set(customerId, false);
        console.log(`Loaded and cached burn lists for customer ${customerId}:`, burnLists);
      }),
      catchError(error => {
        console.error(`Failed to load burn lists for customer ${customerId}`, error);
        this.burnListsLoading.set(customerId, false);
        // Return empty array on error but don't cache it
        return of([]);
      })
    );
  }

  /**
   * Clear all cached data. Useful when data needs to be refreshed.
   */
  clearCache(): void {
    this.burnListStatuses = null;
    this.burnListRegions = null;
    this.burnListTypes = null;
    this.burnLists.clear();
    console.log('Burn list data cache cleared');
  }

  /**
   * Clear specific cache
   */
  clearStatusesCache(): void {
    this.burnListStatuses = null;
  }

  clearRegionsCache(): void {
    this.burnListRegions = null;
  }

  clearTypesCache(): void {
    this.burnListTypes = null;
  }

  /**
   * Clear burn lists cache for a specific customer or all customers
   * @param customerId Optional customer ID. If not provided, clears all burn lists.
   */
  clearBurnListsCache(customerId?: number): void {
    if (customerId !== undefined) {
      this.burnLists.delete(customerId);
      console.log(`Burn lists cache cleared for customer ${customerId}`);
    } else {
      this.burnLists.clear();
      console.log('All burn lists cache cleared');
    }
  }
}
